import {
  CloseButton,
  MenuIcon,
  PersonIcon,
  SearchIcon,
  brandclubStyles,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
  useIsConciergeEnabled,
} from "@brandclub/common-ui";
import { MessageCircle } from "lucide-react";
import { Drawer, styled } from "@mui/material";
import React, { forwardRef, MouseEventHandler } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import {
  DRAWER_Z_INDEX,
  SingleNavType,
  MOBILE_TOP_BAR_HEIGHT,
} from "./constants";
import { SiteBrandingType } from "../../types/misc";
import { useAppSelector } from "../../redux/hooks";
import { useStateWithLocationReset } from "../../utils/hooks/useStateWithLocationReset";
import BagDrawer from "../Checkout/BagDrawer";
import { useCheckoutDrawer } from "../Checkout/useCheckoutDrawer";
import { MobileNavButton, MobileNavCartButton } from "./Buttons/NavCartButton";
import DrawerMenu from "./MobileDrawerMenu";
import SearchDropdown from "./SearchDropdown";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { getHeaderColors } from "@/utils/StringUtils";
import { useIsConciergePage } from "@/utils/hooks/useIsConciergePage";

const Container = styled("div")<{ siteBrandingType: SiteBrandingType }>(
  ({ theme, siteBrandingType }) => ({
    ...getHeaderColors(theme, siteBrandingType),
    height: MOBILE_TOP_BAR_HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    padding: "0 40px",
    boxSizing: "border-box",
    zIndex: 1202,
    position: "relative",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      padding: "0 40px",
    },
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      padding: "0 20px",
    },
    ".logo": {
      height: "100%",
      img: {
        objectFit: "contain",
        height: "auto",
        maxHeight: "100%",
        maxWidth: 125,
      },
    },
    ".buttons": {
      display: "flex",
    },
  })
);

const MobileNav = (
  {
    siteBrandingType,
    persistentQueryParams,
    handleSearchClose,
    onSubmitSearch,
    searchInput,
    setSearchInput,
    handleSearchOpen,
    searchOpen,
    pageContext,
    filterDialogOpen,
    setFilterDialogOpen,
    filterCount,
    setFilterCount,
    cartCount,
    navMenu,
    brandHeaderLogo,
    hideHeader = false,
  }: {
    siteBrandingType: SiteBrandingType;
    persistentQueryParams: string;
    handleSearchClose: () => void;
    onSubmitSearch: () => void;
    searchInput: string;
    setSearchInput: (searchInput: string) => void;
    handleSearchOpen: (event: React.MouseEvent<HTMLDivElement>) => void;
    searchOpen: boolean;
    pageContext: any;
    filterDialogOpen: boolean;
    setFilterDialogOpen: (filterDialogOpen: boolean) => void;
    filterCount: number;
    setFilterCount: (filterCount: number) => void;
    navMenu: SingleNavType[];
    cartCount: number;
    brandHeaderLogo: string;
    hideHeader?: boolean;
  },
  ref: React.Ref<any>
) => {
  const [drawerMenuOpen, setDrawerMenuOpen] = useStateWithLocationReset(false);
  const navigate = useNavigate();
  const { signedIn } = useSignedIn();
  const homePagePathMatch = useMatch("/");
  const onHomePage = homePagePathMatch?.pattern?.end;
  const topBarOffset = Math.max(0, MOBILE_TOP_BAR_HEIGHT - window.scrollY);

  const { isOpen: isCheckoutDrawerOpen, closeDrawer: closeCheckoutDrawer } =
    useCheckoutDrawer();

  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const { showInHeader } = useIsConciergeEnabled({
    email: userProfile?.email,
  });

  const closeDrawer: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    handleSearchClose();
    setDrawerMenuOpen(false);
    closeCheckoutDrawer();
  };

  const isConciergePage = useIsConciergePage();
  const isHeaderVisible = !isConciergePage && !hideHeader;

  return (
    <>
      {isHeaderVisible ? (
        <Container siteBrandingType={siteBrandingType}>
          <div className="logo">
            <Link style={{ display: "flex", height: "100%" }} to={"/"}>
              <img loading="lazy" src={brandHeaderLogo} alt="bd_logo"></img>
            </Link>
          </div>
          <div className="buttons">
            {searchOpen || drawerMenuOpen ? (
              <MobileNavButton siteBrandingType={siteBrandingType}>
                <CloseButton iconSize={16} onClick={closeDrawer} />
              </MobileNavButton>
            ) : (
              <>
                {onHomePage && (
                  <MobileNavButton
                    siteBrandingType={siteBrandingType}
                    onClick={(e: any) => {
                      signedIn
                        ? navigate(`/dashboard/me`)
                        : navigate(`/signin`);
                    }}
                  >
                    <PersonIcon />
                  </MobileNavButton>
                )}
                {!onHomePage && (
                  <MobileNavButton
                    siteBrandingType={siteBrandingType}
                    onClick={handleSearchOpen}
                  >
                    <SearchIcon />
                  </MobileNavButton>
                )}
                {showInHeader && (
                  <MobileNavButton
                    siteBrandingType={siteBrandingType}
                    onClick={() => {
                      navigate("/concierge");
                    }}
                  >
                    <MessageCircle size={20} />
                  </MobileNavButton>
                )}
                <MobileNavCartButton
                  siteBrandingType={siteBrandingType}
                  onClick={() => {
                    navigate("/mybag");
                  }}
                  count={cartCount}
                />
                <MobileNavButton
                  siteBrandingType={siteBrandingType}
                  onClick={() => {
                    setDrawerMenuOpen(true);
                  }}
                >
                  <MenuIcon />
                </MobileNavButton>
              </>
            )}
          </div>
        </Container>
      ) : null}
      <Drawer
        id="login-popover"
        open={searchOpen}
        sx={{
          zIndex: DRAWER_Z_INDEX,
          width: 462,
        }}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100%",
            boxShadow: "none",
            borderRadius: "0",
            ...(brandclubStyles.customScrollbar as any),
          },
        }}
        anchor="top"
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        onClose={handleSearchClose}
      >
        <div
          style={{
            width: "100%",
            padding: "6px 20px",
            boxSizing: "border-box",
          }}
        >
          <div style={{ paddingTop: topBarOffset + 8 }}></div>
          <div>
            <SearchDropdown
              onSubmitSearch={onSubmitSearch}
              onClose={handleSearchClose}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              persistentQueryParams={persistentQueryParams}
              pageContext={pageContext}
              filterDialogOpen={filterDialogOpen}
              setFilterDialogOpen={setFilterDialogOpen}
              filterCount={filterCount}
              setFilterCount={setFilterCount}
            />
          </div>
        </div>
      </Drawer>
      <Drawer
        id="cart-popover"
        open={isCheckoutDrawerOpen}
        sx={{
          zIndex: DRAWER_Z_INDEX,
          width: 462,
          maxWidth: "100%",
        }}
        PaperProps={{
          sx: {
            boxShadow: "none",
            borderRadius: "0",
            maxWidth: "100%",
            ...(brandclubStyles.customScrollbar as any),
          },
        }}
        anchor="right"
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        onClose={() => {
          closeCheckoutDrawer();
        }}
      >
        <div style={{ paddingTop: topBarOffset }}></div>
        <BagDrawer />
      </Drawer>
      <DrawerMenu
        open={drawerMenuOpen}
        setOpen={setDrawerMenuOpen}
        navMenu={navMenu}
        handleLoginOpen={() => { }}
      />
    </>
  );
};

export default forwardRef(MobileNav);
